import { afterNextRender, Component, computed, ElementRef, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { DeliveriesService } from '../settings/account/deliveries/deliveries.service';
import { DeliveryInformation } from '../settings/account/deliveries/intarfaces';
import { CalculationsService } from '../shared/calculations.service';
import { ModalContentTypes } from '../shared/constants/modal-content-types';
import { MINIMUM_SPEND_VALIDATION_METHOD, MinimumSpendValidationMethod } from '../shared/constants/minimum-spend';
import { ModalContentService } from '../shared/modal-content/modal-content.service';
import { OrderService } from '../shared/order.service';
import { ResolutionService } from '../shared/resolution.service';
import { SignalsStoreService } from '../shared/signals-store.service';
import { TipsDonationsService } from '../shared/tips-donations.service';
import { RELATED_ORDER_PRODUCTS, Fee } from '../shared/types/order.type';
import { formatDateToReadableString } from '../shared/utils/formatting';
import { Session } from '../shared/types/session.type';
import { Router, RouterLink } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CommonModule } from '@angular/common';
import { ProductCartPreview } from '../shared/product-card/product-cart-preview/product-cart-preview.component';
import { LocalStorageService } from '../shared/local-storage.service';
import { finalize, tap } from 'rxjs';
import { FirebaseCrudService } from '../shared/firebase-crud.service';
import { EmptyMessageComponent } from '../shared/empty-message/empty-message.component';
import { FormsModule } from '@angular/forms';
import { LOCALSTORAGE_KEYS } from '../shared/constants/databases';
import { SubscriptionsService } from '../settings/account/subscriptions/subscriptions.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { ProductCardV2Component } from '../shared/product-card-v2/product-card-v2.component';
import { SuggestedProductsType } from '../shared/types/common.enums';
import { OrderSliderComponent } from './order-slider/order-slider.component';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { updateFirebaseOrderAndSubTrigger } from '../shared/common/order.utils';
import { PromotionTagsComponent } from '../shared/promotion-tags/promotion-tags.component';
import { dollarFormatter } from '../shared/common/utils';
import { MembershipService } from '../settings/billing/membership/membership.service';
import { MembershipSavingsComponent } from '../shared/membership-savings/membership-savings.component';
import { PastDueBannerComponent } from '../shared/past-due-banner/past-due-banner.component';

@Component({
  selector: 'app-side-checkout',
  providers: [MembershipService],
  imports: [
    PastDueBannerComponent,
    RouterLink,
    MatProgressBarModule,
    CommonModule,
    ProductCartPreview,
    EmptyMessageComponent,
    FormsModule,
    ProductCardV2Component,
    OrderSliderComponent,
    NgbTooltipModule,
    PromotionTagsComponent,
    MembershipSavingsComponent
  ],
  templateUrl: './side-checkout.component.html',
  styleUrl: './side-checkout.component.scss'
})
export class SideCheckoutComponent implements OnInit {
  signalsStoreService = inject(SignalsStoreService);
  private orderService = inject(OrderService);
  private resolutionService = inject(ResolutionService);
  private deliveriesService = inject(DeliveriesService);
  #calculationsService = inject(CalculationsService);
  #eRef = inject(ElementRef);
  #router = inject(Router);
  #modalContentService = inject(ModalContentService);
  #tipsDonationsService = inject(TipsDonationsService);
  #localStorageService = inject(LocalStorageService);
  #firebaseCrudService = inject(FirebaseCrudService);
  #subscriptionsService = inject(SubscriptionsService);
  #membershipService = inject(MembershipService);

  odooOrderProducts: Signal<any> = computed(() => this.setUpOdooProducts());

  isContentLoaded: Signal<any> = computed(() => this.signalsStoreService.isContentLoaded());
  odooOrder: Signal<any | null> = computed(() => this.orderService.odooOrder());
  products: Signal<any | null> = computed(() => this.setUpProducts());
  isMobile = computed(() => this.resolutionService.isMobile());

  carouselBuyAgainProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.BUY_AGAIN, false));
  carouselFavoritesProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.FAVORITES, false));
  carouselSuggestedProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.SUGGESTED));

  accordionOpened = '';

  hasGlobalMessage = computed(() => !!this.signalsStoreService.globalMessages().length);

  hideCrossSells = signal(false);

  #showCartPreview$ = toObservable(this.signalsStoreService.showCartPreview);

  //#region Properties

  showFreeDeliverySlider = signal(environment.config.flows.order.showFreeDeliverySlider);

  hasVoluntaryDonationFlow = signal(environment.config.flows.order.voluntaryDonationFlow);

  progressBarColor = computed(() => this.#setUpProgressBarColor());

  currentSliderInfo = computed(() => this.#setUpCurrentSliderInfo())


  hasDriverTipsFlow = signal(environment.config.flows.order.hasDriveTipsFlow);

  deliveryInfo = computed(() => this.#setUpDeliveryInfo());

  orderSubTotal = computed(() => this.odooOrder()?.paymentDetails?.subTotal || 0);

  orderGrossSubTotal: Signal<number> = computed(() => this.odooOrder()?.paymentDetails?.subtotalBeforeDiscounts || this.odooOrder()?.paymentDetails?.subTotal || 0);

  minimumSpendValidationMethod: Signal<MinimumSpendValidationMethod> = computed(
    () => this.odooOrder()?.paymentDetails?.minimumSpendValidationMethod || MINIMUM_SPEND_VALIDATION_METHOD.after_discounts
  );

  hasPricelistDiscount: Signal<boolean> = computed(() => this.orderGrossSubTotal() > this.orderSubTotal());

  // Subtotal every spend threshold on this order is measured against: the order
  // minimum, the free-delivery threshold and the delivery-fee range alike. Mirrors
  // the backend rule set by `sales_smc.minimum_spend_validation_method` so the UI
  // never disagrees with what the backend charges.
  qualifyingSubtotal: Signal<number> = computed(() =>
    this.minimumSpendValidationMethod() === MINIMUM_SPEND_VALIDATION_METHOD.before_discounts
      ? this.orderGrossSubTotal()
      : this.orderSubTotal()
  );

  totalSavings: Signal<number> = computed(() => this.#getTotalSavings());

  //#region membership savings (DEV-10551)
  hasMembershipFlow = signal(environment.config.flows.membership);
  membershipInfo = computed(() => this.odooOrder()?.paymentDetails?.membership || null);
  membershipSavingsOnOrder = computed(() => this.membershipInfo()?.savingsOnOrder || 0);
  membershipSavingsBreakdown = computed(() => this.membershipInfo()?.savingsBreakdown || null);
  membershipUpgradePreview = computed(() => this.membershipInfo()?.upgradePreview || null);
  hasMembership = computed(() => !!this.signalsStoreService.sessionSignal()?.settings?.hasMembership);

  upgradeMembership() {
    const plan = this.membershipUpgradePreview()?.membership;
    if (!plan) return;
    this.#membershipService.openUpgradeFlow(plan);
  }
  //#endregion

  orderDeliveryFee = computed(() => this.#calculationsService.getOrderDeliveryFee(this.odooOrder(), this.qualifyingSubtotal(), this.products()));

  enableFreeDeliveryThreshold = computed(() => this.#setUpEnableFreeDeliveryThreshold())

  hasDeliveryFeeRange = computed(() => !!this.odooOrder()?.paymentDetails?.useDeliveryFeeRange);

  additionalFees: Signal<Fee[]> = computed(() => this.#calculationsService.calculateOrderAdditionalFees(this.odooOrder(), this.orderSubTotal()));

  hiddenCarouselProducts: Signal<number[]> = computed(() => this.signalsStoreService.hiddenCarouselProducts());

  orderTaxes = computed(() => this.odooOrder()?.paymentDetails?.taxes || 0);

  orderCredits = computed(() => this.#getOrderCredits());

  appliedCredits: Signal<number> = computed(() => this.#getAppliedCredits());

  creditsBalance: Signal<number> = computed(() => this.#getCreditsBalance());

  orderTipAmount = computed(() => this.#getOrderTip());

  orderTotal = computed(() => this.#getTotalOrder());

  isTemporaryRouteChange = signal(false);

  temporaryRouteChangeMessage = signal('');

  orderDonationAmountVoluntary = computed(() => this.#getOrderDonationVoluntaryAmount());

  percentMinSpend: Signal<number> = computed(() => this.#calculatePercentForFreeDeliveryOrAllowOrder());

  minOrderDiffForFreeDelivery: Signal<number> = computed(() => this.#calculateMinOrderDiffForFreeDelivery());

  updateDonation = ModalContentTypes.UPDATE_DONATION;

  createDonation = ModalContentTypes.CREATE_DONATION;

  updateTip = ModalContentTypes.UPDATE_TIP;

  createTip = ModalContentTypes.CREATE_TIP;

  isOpenModal = signal(false);

  addressInfo: Signal<any | null> = computed(() => this.#setUpAddressInfo())

  paymentMethodInfo: Signal<any | null> = computed(() => this.#setUpPaymentMethod())

  isDeliveryDetailsCollapsed = signal(true);

  areSkippedProductsCollapsed = signal(false);

  hasMinOrder: Signal<boolean> = computed(() => this.#hasMinOrder());

  minOrder: Signal<number> = computed(() => this.odooOrder()?.paymentDetails?.minOrder || 0)

  hasPendingChanges: Signal<boolean> = computed(() => this.#checkPendingChanges());

  hasAutoApplicableCoupon: Signal<boolean> = computed(() => this.#checkAutoApplicableCoupon());

  coupon: WritableSignal<string> = signal(''); // actionTrigger

  showCouponErrorMessage: Signal<boolean> = computed(() => this.orderService.showCouponErrorMessage());

  couponErrorMessage: Signal<string> = computed(() => this.orderService.couponErrorMessage());

  skippedSubscriptions: Signal<any | null> = computed(() => this.#setUpSkippedSubscriptions());

  isEmptyOrder: Signal<boolean> = computed(() => {
    return !this.odooOrderProducts()?.subscription.length &&
      !this.odooOrderProducts()?.common.length
  })

  deliveryDetailsText = computed(() => this.#setUpDeliveryDetailsText())

  isSkipped = computed(() => !!this.odooOrder()?.isSkipped);

  isPickup = computed(() => this.signalsStoreService.isAddressRequired() === false);

  isUpdating = signal(false);

  // noCheckoutNeeded = signal(`<p class="m-0 text-center">No checkout needed—your delivery is on the way! Items ship unless removed before the cut-off.</p>`)
  noCheckoutNeeded = computed(() => `<p class="m-0 text-center">No checkout needed–your delivery will be finalized if your order reaches the minimum <strong>(${dollarFormatter.format(this.minOrder())})</strong> required for processing.</p>`)

  suggestedTotals = computed(() => {
    const suggested = this.carouselSuggestedProducts();
    const favorites = this.carouselFavoritesProducts();
    const buyAgain = this.carouselBuyAgainProducts();

    const total = (suggested?.length || 0) + (favorites?.length || 0) + (buyAgain?.length || 0);
    return total > 9 ? `+9` : total;
  });

  orderOnWay: Signal<{ id: number, deliveryDate: string, isOnWay: boolean } | null> = computed(() => this.#setUpOrderOnWay());

  /**
   * This property is isued to validate if the users can submit thier order despite not having reached the minimum price to save the order
   * because the bundle has not been published (its price is equal to zero).
   */
  orderHasUnpublishedBundle: Signal<boolean> = computed(() => {
    const products = this.products();
    const existsUnpublishedSubscriptionBundle = products?.subscription.some((p: any) => p.isBundle && !p?.bundle?.items.length) || false;
    const existsUnpublishedAlaCarteBundle = products?.common.some((p: any) => p.isBundle && !p?.bundle?.items.length) || false;
    return existsUnpublishedAlaCarteBundle || existsUnpublishedSubscriptionBundle;
  });

  marketStatus = computed(() => this.signalsStoreService.marketStatus());
  closedMarket = computed(() => {
    const ms = this.marketStatus();
    return {
      title: `Our Online Market Opens ${ms?.openingDay?.toUpperCase() || ''} Morning!`,
      legend: `We're busy sourcing next week's availability! Check back ${ms?.openingDay || ''} at ${ms?.openingHour || ''} to begin shopping.`
    };
  })

  //#region Preorder Products
  contactEmail = signal('');

  preOrdersSkipDisclaimer = computed(() => {
    const ce = this.contactEmail();
    if (!ce) return '';
    return `You cannot skip this week’s order as it contains pre-ordered products. For assistance, please contact us at ${ce}.`
  })

  preOrderedProductDisclaimer = signal('This pre-ordered product cannot be modified or removed.')
  hasPreorderedProducts = computed(() => {
    const odooOrder = this.odooOrder();
    return (odooOrder?.products?.common || []).some((p: { isPreOrder: boolean; }) => p.isPreOrder);
  });

  permissionsSetup = computed(() => this.#validatePermissions());

  suggestedProductsType = SuggestedProductsType;

  isInShopComponent = computed(() => this.signalsStoreService.getIsInShopComponent());

  //#endregion

  //#endregion

  constructor() {
    afterNextRender(() => {
      this.#validateTemporaryRoute();
      this.contactEmail.set(environment.config.contactEmail);
      this.#subscribeToCartVisibilityChanges();
    });

    // toObservable(this.suggestedProducts)
    //   .subscribe(val => !val && this.hideCrossSells.set(this.isMobile()));

    toObservable(this.isMobile)
      .subscribe(val => this.hideCrossSells.set(val))
  }

  ngOnInit(): void {
    if (this.isMobile()) {
      this.orderService.getOrder(this.signalsStoreService.marketStatus);
    }
  }

  setUpOdooProducts() {
    const odooOrder = this.odooOrder();

    if (!odooOrder || !odooOrder?.products)
      return null

    if (!odooOrder?.products.common?.length && !odooOrder?.products.subscription?.length)
      return null

    const odooCommonProducts: any[] = odooOrder?.products.common;
    const odooSubscriptionProducts: any[] = odooOrder?.products.subscription;
    return { common: (odooCommonProducts || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)), subscription: (odooSubscriptionProducts || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)) };
  }

  private setUpProducts(): any {
    const odooOrder = this.odooOrder();

    const {
      common: odooCommonProducts = [],
      subscription: odooSubscriptionProducts = []
    } = odooOrder?.products || {};

    return {
      common: odooCommonProducts,
      subscription: odooSubscriptionProducts,
    };
  }

  private setUpCarouselItems(orderProductType: string, isOrderSuggestedProduct: boolean = true) {
    const hiddenProducts: number[] = this.hiddenCarouselProducts();
    if (this.odooOrder()?.isSkipped) return [];

    if (!this.isContentLoaded || !this.odooOrder()?.relatedProducts?.[orderProductType] || !this.odooOrder()?.relatedProducts?.[orderProductType]?.length)
      if (!this.odooOrder()?.relatedProducts?.[orderProductType] || !this.odooOrder()?.relatedProducts?.[orderProductType]?.length)
        return []

    let items: any = this.odooOrder()?.relatedProducts?.[orderProductType] || [];

    if (isOrderSuggestedProduct) {
      const allProductsId = [...this.products().common, ...this.products().subscription].map((product: any) => product.id)
      items = items.filter((product: any) => !allProductsId.includes(product.id));
    }

    if (hiddenProducts.length)
      items = items.filter((product: any) => hiddenProducts.indexOf(product.variantId) === -1)


    items = items.map((product: any) => {
      product.image = 'assets/images/logo.webp';
      product.name = product.name;
      if (typeof product?.price === 'number')
        product.price = product.price.toFixed(2);

      return ({
        orderId: this.odooOrder()?.id,
        productDetails: { ...product, isSubscription: false },
        settings: { isCardInOrderPage: true, hideWhenWasOOS: true, isCrossSell: true }
      })
    })

    return items
  }

  //#region Methods

  #setUpDeliveryInfo() {
    if (!this.deliveriesService.deliveryZoneInfo()) return null

    const { deliveryDate, cutoffDate, cutoffTime, deliveryWindow } = (this.deliveriesService.deliveryZoneInfo()?.order || this.deliveriesService.deliveryZoneInfo()) as DeliveryInformation;
    const deliveryDateFormatted = formatDateToReadableString(deliveryDate)
    const cutoffDateFormatted = formatDateToReadableString(cutoffDate)

    const deliveryPickupDateText = this.signalsStoreService.isAddressRequired() ? 'Delivery Date: ' : 'Pick Up Date: ';

    const cutoffTimeFormatted = (cutoffTime || '').replace(/\./g, '').toUpperCase();
    return {
      deliveryWindow,
      deliveryDateText: `${this.odooOrder()?.isSkipped ? 'Next' : ''} ${deliveryPickupDateText}${deliveryDateFormatted.monthNumber}/${deliveryDateFormatted.dayNumber}/${deliveryDateFormatted.year}`,
      cutoffDateText: `${cutoffDateFormatted.dayName} ${cutoffDateFormatted.month} ${cutoffDateFormatted.day} at ${cutoffTimeFormatted}`,
      thanksMessage: this.signalsStoreService.isAddressRequired() ? 'Get excited for your delivery, which will arrive on:' : 'Your order will be ready for pick-up on:',
      mobileDeliveryDateText: `${deliveryDateFormatted.mobileDayName} ${deliveryDateFormatted.mobileFormat}`,
      mobileCoutoffText: `${cutoffDateFormatted.mobileDayName} ${cutoffDateFormatted.mobileFormat} ${cutoffTimeFormatted}`,
      deliveryDate: deliveryDateFormatted.mmddyyyyFormat
    };
  }

  #calculatePercentForFreeDeliveryOrAllowOrder() {
    const hasFreeDeliveryValidation = this.enableFreeDeliveryThreshold();
    const hasDeliveryFeeRange = this.hasDeliveryFeeRange();
    const currentDeliveryFeeInfo = this.currentSliderInfo();
    const subtotal = this.qualifyingSubtotal();
    const minSpend = currentDeliveryFeeInfo.minSpend;
    const minOrder = this.minOrder();
    const hasMinOrder = this.hasMinOrder();
    return (subtotal * 100) / (((hasFreeDeliveryValidation || hasDeliveryFeeRange) && hasMinOrder) ? minSpend : minOrder);
  }

  #getTotalOrder() {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder());
    return this.#calculationsService.getOrderTotal(this.odooOrder(), products);
  }

  #getOrderTip() {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder());
    return this.#calculationsService.getTipAmount(this.odooOrder(), products);
  }

  #getOrderCredits() {
    return this.odooOrder()?.paymentDetails?.credits || 0;
  }

  #getOrderDonationVoluntaryAmount() {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder());
    return this.#calculationsService.getDonationVoluntaryAmount(this.odooOrder(), products);
  }

  goToMarket() {
    if (!this.isInShopComponent())
      this.#router.navigate(['/shop']);
    this.signalsStoreService.showCartPreview.set(false);
  }

  openPaymentMethodModal() {
    this.#modalContentService.openModal(ModalContentTypes.PAYMENT_METHOD, {
      title: 'Update payment method',
      closeable: true,
    });
  }

  openDeliveriesModal() {
    this.#modalContentService.openModal(ModalContentTypes.DELIVERIES, {
      title: 'Manage deliveries',
      closeable: true,
    });
  }

  openModalTip(action: string) {

    this.signalsStoreService.odooTipAmount.set(this.orderTipAmount() ?? 0);
    this.isOpenModal.set(true);

    this.#modalContentService
      .openModal(ModalContentTypes.TIP)
      .closed
      .subscribe((res) => {

        setTimeout(() => {
          this.isOpenModal.set(false);
        }, 500);

        if (!res?.amount) return;

        const payload = {
          amount: res?.amount,
          isRecurrent: res?.isRecurrent,
          applyToAllOrders: res?.applyToAllOrders
        }

        if (action === this.createTip) {
          this.#tipsDonationsService.create(payload, ModalContentTypes.TIP);
        } else {
          this.#tipsDonationsService.update(payload, ModalContentTypes.TIP);
        }
      })
  }

  deleteDonationVoluntary() {
    if (this.odooOrder()?.paymentDetails?.donation?.isRecurrent) {
      this.#modalContentService.openModal(ModalContentTypes.DELETE_TIP_AND_DONATION)
        .closed
        .subscribe((res) => {
          const applyToAllOrders = Number(res?.applyToAllOrders);
          this.#tipsDonationsService.delete(applyToAllOrders, ModalContentTypes.DONATION);
        })
    } else {
      this.#tipsDonationsService.delete(0, ModalContentTypes.DONATION);
    }
  }

  deleteTip() {
    this.isOpenModal.set(true);

    if (this.odooOrder()?.paymentDetails?.tip?.isRecurrent) {
      this.#modalContentService
        .openModal(ModalContentTypes.DELETE_TIP_AND_DONATION)
        .closed
        .subscribe((res) => {

          setTimeout(() => {
            this.isOpenModal.set(false);
          }, 500);

          const applyToAllOrders = Number(res?.applyToAllOrders);
          this.#tipsDonationsService.delete(applyToAllOrders, ModalContentTypes.TIP);
        });
    } else {
      this.#tipsDonationsService.delete(0, ModalContentTypes.TIP);
    }
  }

  checkPendingChanges() {
    return !!(this.products() &&
      [...this.products()?.common, ...this.products()?.subscription].some(product => product.hasPendingChanges));
  }

  //#region Order Info
  #setUpAddressInfo() {
    const session: Session = this.signalsStoreService.sessionSignal() as any;

    if (!session) return null;

    const { street, city, zip: zipCode, stateCode: state, additional } = session.address;
    const { location, name } = session?.pickUpInfo || {};

    const isAddressRequired = this.signalsStoreService.isAddressRequired();
    return {
      title: isAddressRequired ? 'Delivery Address' : 'Pick Up Address',
      text: isAddressRequired ? [street, city, state, zipCode].join(', ') : `${name} ${location ? `at ${location}` : ''}`,
      additional
    };
  }

  #setUpPaymentMethod() {
    const session: Session = this.signalsStoreService.sessionSignal() as any;

    if (!session) return null;

    return this.signalsStoreService.sessionSignal()?.creditCard?.number || '****'
  }

  #checkPendingChanges() {
    return !!(this.products() &&
      [...this.products()?.common, ...this.products()?.subscription].some(product => product.hasPendingChanges));
  }

  #checkAutoApplicableCoupon() {
    const odooOrder = this.odooOrder();
    if (!odooOrder?.paymentDetails?.coupons?.length) return false;
    const exists = odooOrder.paymentDetails.coupons.some((e: any) => e.autoApply);
    return exists;
  }
  #setUpSkippedSubscriptions() {

    const skippedSubscriptions = this.odooOrder()?.skippedSubscriptions || [];
    return skippedSubscriptions;
  }

  closeSideCart() {
    this.signalsStoreService.showCartPreview.set(false);
  }

  onChangeSkipWeekDelivery(unskip: boolean = false) {
    if (!unskip && !this.odooOrder()?.isSkipped) {
      if (environment.config.flows.order.askForDonationOnSkip)
        return this.openModalDonationSkippable();
      return this.openModalSkipWithDonationFlow();
    }

    const odooOrder = this.odooOrder();
    if (!odooOrder?.id) return

    const args = {
      orderId: +odooOrder.id,
      isSkipping: false,
      donationAmount: 0,
    }

    this.#skipOrUnskipOrder(args);
  }

  openModalDonationSkippable() {
    this.signalsStoreService.totalOrderAmount.set(this.odooOrder()?.paymentDetails?.subTotal || 0)
    this.#modalContentService.openModal(ModalContentTypes.DONATION_SKIPPABLE).closed
      .subscribe((res) => {
        if (!('isDonationCanceled' in res) || res?.isDonationCanceled) return;

        const odooOrder = this.odooOrder();
        if (!odooOrder?.id) return;
        const args = {
          orderId: +odooOrder.id,
          isSkipping: true,
          donationAmount: res?.donationAmount || 0,
        }
        this.#skipOrUnskipOrder(args);
      })
  }

  openModalSkipWithDonationFlow() {
    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Are you sure?',
      textContent: `You are about to skip your delivery and you will not be receiving your order this week. Please note that any a la carte products will need to be re-added in future orders.`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Confirm',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) return;
        const odooOrder = this.odooOrder();
        if (!odooOrder?.id) return;
        const args = {
          orderId: +odooOrder.id,
          isSkipping: true,
          donationAmount: 0,
        }
        this.#skipOrUnskipOrder(args);
      })
  }

  #skipOrUnskipOrder(args: any) {
    this.orderService.skipOrder(args).subscribe({
      next: () => {
        updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService);
        this.#subscriptionsService.get()
      }
    });
  }


  #setUpDeliveryDetailsText() {
    const isAddressRequired = this.signalsStoreService.isAddressRequired();
    const isOrderSkipped = !!this.odooOrder()?.isSkipped;

    return isOrderSkipped ? `You've Skipped Your Order!` : isAddressRequired ? `Your Delivery Details` : `Your Pick Up Details`
  }

  #getCreditsBalance() {
    return this.odooOrder()?.paymentDetails?.creditsBalance || 0;
  }

  #getAppliedCredits() {
    return this.odooOrder()?.paymentDetails?.creditsApplied || 0;
  }

  applyCoupon() {
    if (this.isUpdating())
      return;

    this.isUpdating.set(true);

    this.orderService.applyCouponWithOrder(this.coupon, +this.odooOrder().id)
      .pipe(
        finalize(() => this.isUpdating.set(false))
      ).subscribe();
  }

  openModalDonation(action: string) {
    if (this.orderDonationAmountVoluntary())
      this.signalsStoreService.odooDonationAmountVoluntary.set(this.orderDonationAmountVoluntary());

    this.#modalContentService.openModal(ModalContentTypes.DONATION)
      .closed
      .subscribe((res) => {
        if (!res?.amount) return;

        const payload = {
          amount: res?.amount,
          isRecurrent: res?.isRecurrent,
          applyToAllOrders: res?.applyToAllOrders
        }

        if (action === ModalContentTypes.CREATE_DONATION) {
          this.#tipsDonationsService.create(payload, ModalContentTypes.DONATION);
        } else if (action === ModalContentTypes.UPDATE_DONATION) {
          this.#tipsDonationsService.update(payload, ModalContentTypes.DONATION);
        }
      })
  }

  #getTotalSavings() {
    return this.#calculationsService.getOrderTotalSavings({
      subtotal: this.orderSubTotal(),
      grossSubtotal: this.orderGrossSubTotal(),
    })
  }

  #hasMinOrder(): boolean {
    return this.qualifyingSubtotal() >= this.minOrder();
  }

  #validateTemporaryRoute() {

    const currentSessionInfo: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!currentSessionInfo)
      return;

    const { temporaryChange } = currentSessionInfo?.deliveryInfo ?? {};
    this.isTemporaryRouteChange.set(!!temporaryChange?.active);

    if (this.isTemporaryRouteChange())
      this.temporaryRouteChangeMessage.set(temporaryChange?.message ?? '');
  }

  #validatePermissions() {
    const permissions = this.signalsStoreService.permissions();
    const hasDeliveriesPermission = !!permissions?.settings.deliveries.allowed;
    const hasSubscripcionPermission = !!permissions?.settings.subscriptions.allowed;
    const hasPaymentMethodPermission = !!permissions?.settings.paymentMethod.allowed;

    return { hasDeliveriesPermission, hasSubscripcionPermission, hasPaymentMethodPermission };
  }

  #calculateMinOrderDiffForFreeDelivery() {
    const currentDeliveryFeeInfo = this.currentSliderInfo();
    const subtotal = this.qualifyingSubtotal();
    const fee = currentDeliveryFeeInfo.minSpend;
    return fee - subtotal;
  }

  #setUpProgressBarColor() {
    const hasMinOrde = this.#hasMinOrder();
    const fee = this.orderDeliveryFee();
    const hasValidation = this.hasVoluntaryDonationFlow();
    if (!hasMinOrde) return 'warn';

    if (fee > 0 && hasValidation) return 'accent';

    return 'primary'
  }

  #setUpCurrentSliderInfo() {
    const hmo = this.hasMinOrder();
    if (!hmo) {
      return {
        minSpend: 0,
        maxSpen: this.minOrder(),
        amount: 0,
        isMinOrder: true,
      }
    }

    let orderDeliveryFeeData = this.odooOrder()?.paymentDetails?.deliveryFee || null;
    let deliveryFee = [];
    if (this.odooOrder()?.paymentDetails.useDeliveryFeeRange) {
      deliveryFee = orderDeliveryFeeData || [];
      if (deliveryFee.length) {
        for (const element of deliveryFee) {
          if (this.qualifyingSubtotal() < element.minSpend) {
            return {
              minSpend: element.minSpend,
              maxSpen: element.maxSpend,
              amount: element.amount,
              isMinOrder: false,
            }
          }
        }
      }
    } else {
      deliveryFee = [{
        minSpend: orderDeliveryFeeData.minSpend,
        maxSpen: orderDeliveryFeeData.minSpend,
        amount: orderDeliveryFeeData.amount,
        isMinOrder: false,
        isLast: true
      }]
    }


    const last = deliveryFee[deliveryFee.length - 1];
    if (!last) {
      return {
        minSpend: 0,
        maxSpen: 0,
        amount: this.orderDeliveryFee(),
        isMinOrder: false,
        isLast: true
      }
    } else {
      last.isLast = true;
      last.isMinOrder = false;
    }

    return last;

  }

  //#endregion

  toggleCrollSells(value: boolean) {
    this.hideCrossSells.set(value);

    if (this.isMobile()) {
      this.#trackMobileCrossSellsVisibility(value);
    }
  }

  #trackMobileCrossSellsVisibility(value: boolean) {
    // this.showMobileCartOverlay.set(value);
  }

  async viewAll(productsObject: any, type: SuggestedProductsType) {
    try {
      let textContent = type === SuggestedProductsType.marketplace ?
        'Complement your purchase with these Marketplace items.' :
        type === SuggestedProductsType.favorite ?
          'Revisit your favorite Marketplace items!' :
          'Based on your past orders – buy again with ease!';

      let title = type === SuggestedProductsType.marketplace ?
        'More from Our Marketplace' :
        type === SuggestedProductsType.favorite ?
          'Favorite Products' :
          'Buy Again Products';

      textContent += `<br>Items you add from this section will be delivered on <strong><em>${this.deliveryInfo()?.deliveryDate}</em></strong>`;

      const suggestedProducts = productsObject.map((e: any) => e.productDetails);

      // Ensure the modal service and components are properly loaded before opening
      if (this.#modalContentService && ModalContentTypes.SUGGESTED_PRODUCTS) {
        // Force ensure the component is available by checking if it exists
        await new Promise(resolve => setTimeout(resolve, 0)); // Allow microtask to complete

        this.#modalContentService
          .openModal(ModalContentTypes.SUGGESTED_PRODUCTS, {
            title,
            textContent,
            suggestedProducts
          });
      } else {
        console.error('Modal service or SUGGESTED_PRODUCTS type not available');
      }
    } catch (error) {
      console.error('Error opening suggested products modal:', error);
    }
  }

  #subscribeToCartVisibilityChanges() {
    this.#showCartPreview$.pipe(tap(res => {
      if (res) {
        this.hideCrossSells.set(false);
        this.isDeliveryDetailsCollapsed.set(true);
      }
    })).subscribe()
  }

  #setUpEnableFreeDeliveryThreshold() {
    const order = this.odooOrder();
    if (order.paymentDetails.useDeliveryFeeRange) return false;

    return !!order.paymentDetails.deliveryFee.enableFreeDeliveryThreshold;
  }

  #setUpOrderOnWay() {
    // return {
    //   id: 1,
    //   deliveryDate: '06/17/2025',
    //   isOnWay: true
    // }
    const orderOnWay = this.odooOrder()?.activeOrderWeek || null;
    if (!orderOnWay) return null;

    return {
      id: orderOnWay.id,
      deliveryDate: formatDateToReadableString(orderOnWay.date).mmddyyyyFormat,
      isOnWay: orderOnWay.isOnWay || false
    }
  }


  //#endregion



}
